// ========================================================
// SUBSCRIPTION OVERRIDE MANAGER
// Admin component for manual subscription management
// ========================================================

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { 
  Gift, 
  Users, 
  Clock, 
  CheckCircle,
  XCircle
} from 'lucide-react';
import apiService from '../services/SupabaseApiService';

const ApiService = apiService;

const SubscriptionOverrideManager = () => {
  const [activeSubTab, setActiveSubTab] = useState('active');
  const [activeOverrides, setActiveOverrides] = useState([]);
  const [overrideHistory, setOverrideHistory] = useState([]);
  const [eligibleFamilies, setEligibleFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFamilies, setSelectedFamilies] = useState([]);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [selectedFamilyForOverride, setSelectedFamilyForOverride] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [overrideForm, setOverrideForm] = useState({
    override_tier: 'premium',
    override_reason: 'promotion',
    reason_notes: '',
    expires_at: '',
    billing_paused: true
  });
  const [filters, setFilters] = useState({
    reason: 'all',
    tier: 'all',
    expiring_soon: false
  });

  useEffect(() => {
    loadOverrideData();
  }, []);

  const loadOverrideData = async () => {
    try {
      setLoading(true);
      const [activeData, historyData, eligibleData] = await Promise.all([
        ApiService.getActiveOverrides(),
        ApiService.getOverrideHistory(),
        ApiService.getEligibleFamiliesForPromotion()
      ]);
      
      setActiveOverrides(activeData || []);
      setOverrideHistory(historyData || []);
      setEligibleFamilies(eligibleData || []);
    } catch (error) {
      console.error('Failed to load override data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyOverride = async (familyId, overrideData) => {
    try {
      console.log('🔄 APPLYING OVERRIDE:', { familyId, overrideData });
      
      const result = await ApiService.applySubscriptionOverride(familyId, {
        ...overrideData,
        created_by_admin: 'admin@carekey.us'
      });
      
      console.log('📊 OVERRIDE RESULT:', result);
      
      if (result && result.success) {
        console.log('✅ Override successful, reloading data...');
        await loadOverrideData();
        setShowOverrideModal(false);
        setSelectedFamilyForOverride(null);
        setOverrideForm({
          override_tier: 'premium',
          override_reason: 'promotion',
          reason_notes: '',
          expires_at: '',
          billing_paused: true
        });
        toast.success('✅ Override applied successfully!', {
          style: {
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            borderRadius: '12px',
          },
        });
      } else {
        console.error('❌ Override failed:', result);
        toast.error('❌ Override failed. Please try again.', {
          style: {
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '12px',
          },
        });
      }
    } catch (error) {
      console.error('💥 Error applying override:', error);
      toast.error('💥 Error: ' + error.message, {
        style: {
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '12px',
        },
      });
    }
  };

  const handleBulkOverride = async () => {
    try {
      const result = await ApiService.bulkApplyOverrides(selectedFamilies, {
        ...overrideForm,
        created_by_admin: 'admin@carekey.us'
      });
      
      if (result.success) {
        await loadOverrideData();
        setShowBulkModal(false);
        setSelectedFamilies([]);
      }
    } catch (error) {
      console.error('Failed to apply bulk override:', error);
    }
  };

  const handleRevertOverride = async (familyId) => {
    try {
      const result = await ApiService.revertSubscriptionOverride(familyId, 'Manually reverted by admin');
      if (result.success) {
        await loadOverrideData();
      }
    } catch (error) {
      console.error('Failed to revert override:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDaysRemaining = (expiresAt) => {
    const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getStatusBadge = (status, daysRemaining) => {
    if (status === 'Active') {
      if (daysRemaining <= 3) {
        return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">Expiring Soon</span>;
      } else if (daysRemaining <= 7) {
        return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Expiring</span>;
      }
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Active</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">{status}</span>;
  };

  const filteredActiveOverrides = activeOverrides.filter(override => {
    if (filters.reason !== 'all' && override.override_reason !== filters.reason) return false;
    if (filters.tier !== 'all' && override.override_tier !== filters.tier) return false;
    if (filters.expiring_soon && getDaysRemaining(override.expires_at) > 7) return false;
    return true;
  });

  const overrideReasons = [
    { value: 'promotion', label: '🎁 Promotion', color: 'bg-purple-100 text-purple-800' },
    { value: 'support', label: '🛠️ Support', color: 'bg-blue-100 text-blue-800' },
    { value: 'testing', label: '🧪 Testing', color: 'bg-green-100 text-green-800' },
    { value: 'partnership', label: '🤝 Partnership', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'emergency', label: '📞 Emergency', color: 'bg-red-100 text-red-800' },
    { value: 'other', label: '📝 Other', color: 'bg-gray-100 text-gray-800' }
  ];

  const subscriptionTiers = [
    { value: 'free', label: 'Free', price: '$0' },
    { value: 'premium', label: 'Premium', price: '$19.99' },
    { value: 'family_plus', label: 'Family+', price: '$39.99' }
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Subscription Overrides</h2>
          <p className="text-gray-600">Manage manual subscription changes and promotions</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={() => setShowBulkModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
          >
            <Gift className="h-4 w-4 mr-2" />
            Bulk Promotion
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Overrides</dt>
                  <dd className="text-lg font-medium text-gray-900">{activeOverrides.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-8 w-8 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Expiring Soon</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {activeOverrides.filter(o => getDaysRemaining(o.expires_at) <= 7).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-8 w-8 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Eligible for Promotion</dt>
                  <dd className="text-lg font-medium text-gray-900">{eligibleFamilies.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Gift className="h-8 w-8 text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Overrides</dt>
                  <dd className="text-lg font-medium text-gray-900">{overrideHistory.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sub Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'active', label: 'Active Overrides', count: activeOverrides.length },
            { id: 'history', label: 'History', count: overrideHistory.length },
            { id: 'eligible', label: 'Eligible Families', count: eligibleFamilies.length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeSubTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      {activeSubTab === 'active' && (
        <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
          <select
            value={filters.reason}
            onChange={(e) => setFilters({ ...filters, reason: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">All Reasons</option>
            {overrideReasons.map(reason => (
              <option key={reason.value} value={reason.value}>{reason.label}</option>
            ))}
          </select>
          
          <select
            value={filters.tier}
            onChange={(e) => setFilters({ ...filters, tier: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">All Tiers</option>
            {subscriptionTiers.map(tier => (
              <option key={tier.value} value={tier.value}>{tier.label}</option>
            ))}
          </select>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={filters.expiring_soon}
              onChange={(e) => setFilters({ ...filters, expiring_soon: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Expiring within 7 days</span>
          </label>
        </div>
      )}

      {/* Content */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {activeSubTab === 'active' && (
          <div>
            {filteredActiveOverrides.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No active overrides</h3>
                <p className="mt-1 text-sm text-gray-500">No subscription overrides match your current filters.</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {filteredActiveOverrides.map((override) => {
                  const daysRemaining = getDaysRemaining(override.expires_at);
                  const reason = overrideReasons.find(r => r.value === override.override_reason);
                  
                  return (
                    <li key={override.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h3 className="text-sm font-medium text-gray-900">{override.family_name}</h3>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${reason?.color}`}>
                              {reason?.label}
                            </span>
                            {getStatusBadge('Active', daysRemaining)}
                          </div>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span>{override.parent_email}</span>
                            <span>•</span>
                            <span>
                              {override.original_tier} → {override.override_tier}
                            </span>
                            <span>•</span>
                            <span>Expires {formatDate(override.expires_at)}</span>
                            <span>•</span>
                            <span className={daysRemaining <= 3 ? 'text-red-600 font-medium' : ''}>
                              {daysRemaining} days remaining
                            </span>
                          </div>
                          {override.reason_notes && (
                            <p className="mt-1 text-sm text-gray-600">{override.reason_notes}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleRevertOverride(override.family_id)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Revert
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {activeSubTab === 'history' && (
          <div>
            {overrideHistory.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No override history</h3>
                <p className="mt-1 text-sm text-gray-500">No subscription overrides have been created yet.</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {overrideHistory.slice(0, 50).map((override) => {
                  const reason = overrideReasons.find(r => r.value === override.override_reason);
                  
                  return (
                    <li key={override.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h3 className="text-sm font-medium text-gray-900">{override.family_name}</h3>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${reason?.color}`}>
                              {reason?.label}
                            </span>
                            {getStatusBadge(override.status)}
                          </div>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span>{override.parent_email}</span>
                            <span>•</span>
                            <span>
                              {override.original_tier} → {override.override_tier}
                            </span>
                            <span>•</span>
                            <span>Created {formatDate(override.created_at)}</span>
                            {override.expired_at && (
                              <>
                                <span>•</span>
                                <span>Ended {formatDate(override.expired_at)}</span>
                              </>
                            )}
                          </div>
                          {override.reason_notes && (
                            <p className="mt-1 text-sm text-gray-600">{override.reason_notes}</p>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {activeSubTab === 'eligible' && (
          <div>
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Families Eligible for Promotion</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSelectedFamilies(eligibleFamilies.map(f => f.id))}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedFamilies([])}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
            {eligibleFamilies.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No eligible families</h3>
                <p className="mt-1 text-sm text-gray-500">All families currently have active overrides or don't meet criteria.</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {eligibleFamilies.map((family) => (
                  <li key={family.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedFamilies.includes(family.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedFamilies([...selectedFamilies, family.id]);
                            } else {
                              setSelectedFamilies(selectedFamilies.filter(id => id !== family.id));
                            }
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">{family.family_name}</h3>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span>{family.parent_email}</span>
                            <span>•</span>
                            <span>Current: {family.subscription_tier}</span>
                            <span>•</span>
                            <span>Joined {formatDate(family.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedFamilyForOverride(family);
                          // Set default expiry to 30 days from now
                          const defaultExpiry = new Date();
                          defaultExpiry.setDate(defaultExpiry.getDate() + 30);
                          setOverrideForm({
                            ...overrideForm,
                            expires_at: defaultExpiry.toISOString().slice(0, 16)
                          });
                          setShowOverrideModal(true);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                      >
                        Apply Override
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {selectedFamilies.length > 0 && (
              <div className="px-6 py-4 bg-blue-50 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-blue-800">
                    {selectedFamilies.length} families selected
                  </span>
                  <button
                    onClick={() => setShowBulkModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Apply Bulk Override
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk Override Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Bulk Subscription Override</h3>
                <button
                  onClick={() => setShowBulkModal(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">Override Tier</label>
                  <select
                    value={overrideForm.override_tier}
                    onChange={(e) => setOverrideForm({ ...overrideForm, override_tier: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {subscriptionTiers.filter(tier => tier.value !== 'free').map(tier => (
                      <option key={tier.value} value={tier.value}>
                        {tier.label} ({tier.price}/month)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">Reason</label>
                  <select
                    value={overrideForm.override_reason}
                    onChange={(e) => setOverrideForm({ ...overrideForm, override_reason: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {overrideReasons.map(reason => (
                      <option key={reason.value} value={reason.value}>{reason.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">Expires At</label>
                  <input
                    type="datetime-local"
                    value={overrideForm.expires_at}
                    onChange={(e) => setOverrideForm({ ...overrideForm, expires_at: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">Notes</label>
                  <textarea
                    value={overrideForm.reason_notes}
                    onChange={(e) => setOverrideForm({ ...overrideForm, reason_notes: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Optional notes about this override..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="billing_paused"
                    checked={overrideForm.billing_paused}
                    onChange={(e) => setOverrideForm({ ...overrideForm, billing_paused: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <label htmlFor="billing_paused" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                    Pause Stripe billing during override
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowBulkModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkOverride}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Apply to {selectedFamilies.length} Families
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Individual Override Modal */}
      {showOverrideModal && selectedFamilyForOverride && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-full max-w-md shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 text-center">
                Apply Override to {selectedFamilyForOverride.family_name}
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  {selectedFamilyForOverride.parent_email}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  Current: {selectedFamilyForOverride.subscription_tier}
                </p>
              </div>
              
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">Override Tier</label>
                  <select
                    value={overrideForm.override_tier}
                    onChange={(e) => setOverrideForm({ ...overrideForm, override_tier: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border-2 rounded-md shadow-sm focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: document.documentElement.classList.contains('dark') ? '#1f2937' : 'white',
                      borderColor: document.documentElement.classList.contains('dark') ? '#4b5563' : '#d1d5db',
                      color: document.documentElement.classList.contains('dark') ? 'white' : 'black'
                    }}
                  >
                    {subscriptionTiers.filter(tier => tier.value !== 'free').map(tier => (
                      <option key={tier.value} value={tier.value}>
                        {tier.label} ({tier.price}/month)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">Reason</label>
                  <select
                    value={overrideForm.override_reason}
                    onChange={(e) => setOverrideForm({ ...overrideForm, override_reason: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {overrideReasons.map(reason => (
                      <option key={reason.value} value={reason.value}>{reason.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">Expires At</label>
                  <input
                    type="datetime-local"
                    value={overrideForm.expires_at}
                    onChange={(e) => setOverrideForm({ ...overrideForm, expires_at: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">Notes</label>
                  <textarea
                    value={overrideForm.reason_notes}
                    onChange={(e) => setOverrideForm({ ...overrideForm, reason_notes: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Optional notes about this override..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="billing_paused_individual"
                    checked={overrideForm.billing_paused}
                    onChange={(e) => setOverrideForm({ ...overrideForm, billing_paused: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="billing_paused_individual" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                    Pause Stripe billing during override
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowOverrideModal(false);
                    setSelectedFamilyForOverride(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleApplyOverride(selectedFamilyForOverride.id, overrideForm)}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Apply Override
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionOverrideManager;